import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { EventDocumentType } from '@application/enums/event-document-type';
import { CollaboratorRole } from '@application/enums/collaborator-role';
import { getVDKEventByIdForEventDetailsAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { formatEventDateRange } from '@application/utilities/date-helper';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import TextEditor from '@ui/components/TextEditor';
import DOMPurify from 'dompurify';
import { Buildings, Calendar, KeySquare, Lock, People, UserCirlceAdd, UserSquare } from 'iconsax-react';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-quill/dist/quill.snow.css';
import { useParams } from 'react-router-dom';
import EventCard from '../../components/EventCard';
import AvatarBox from './components/AvatarBox';
import { Icon } from '@iconify/react';
import { ExpandMore } from '@mui/icons-material';
import { PdfIcon } from '@ui/assets/custom-icons/pdf-icon';
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.mkv', '.avi'];
const isVideoDocument = (url, documentName) => {
    const urlPath = url.split('?')[0].toLowerCase();
    const name = (documentName ?? '').toLowerCase();
    return VIDEO_EXTENSIONS.some((ext) => urlPath.includes(ext) || name.endsWith(ext));
};
const isPdfDocument = (url, documentName) => {
    const urlPath = url.split('?')[0].toLowerCase();
    const name = (documentName ?? '').toLowerCase();
    return /\.pdf$/i.test(urlPath) || /\.pdf$/i.test(name);
};
const DetailsTab = forwardRef(({ onEditClick }, ref) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    useImperativeHandle(ref, () => ({
        handleEditClick: (fieldKey) => {
            onEditClick?.(fieldKey);
        }
    }));
    const { eventId } = useParams();
    const { VDKEventsForEventDetails, status } = useAppSelector((state) => state.VDKEvent);
    useEffect(() => {
        if (eventId) {
            dispatch(getVDKEventByIdForEventDetailsAsync({
                id: eventId,
                documentType: EventDocumentType.BackgroundImage
            }));
        }
    }, [dispatch, eventId]);
    const formatDateRange = (startDate, endDate) => {
        return formatEventDateRange(startDate, endDate);
    };
    const cleanDescriptionFloorPlan = useMemo(() => {
        if (!VDKEventsForEventDetails?.floorPlan)
            return '';
        const normalized = VDKEventsForEventDetails.floorPlan.replace(/\n/g, ' ');
        return DOMPurify.sanitize(normalized, {
            ADD_ATTR: ['style', 'class']
        });
    }, [VDKEventsForEventDetails?.floorPlan]);
    // const cleanDescriptionDetails = useMemo(() => {
    //   console.log('Raw Details:', VDKEventsForEventDetails?.details);
    //   if (VDKEventsForEventDetails?.details) {
    //     let normalized = VDKEventsForEventDetails.details.replace(/\n/g, ' ');
    //     let sanitized = DOMPurify.sanitize(normalized, {
    //       ADD_ATTR: ['style', 'class']
    //     });
    //     return sanitized;
    //   }
    //   return '';
    // }, [VDKEventsForEventDetails?.details]);
    const cleanDescriptionDetails = useMemo(() => {
        if (VDKEventsForEventDetails?.details) {
            let normalized = VDKEventsForEventDetails.details.replace(/\n/g, ' ');
            let sanitized = DOMPurify.sanitize(normalized, {
                ALLOWED_ATTR: ['style', 'class', 'href', 'src', 'alt'],
                ALLOWED_TAGS: [
                    // Default allowed tags plus any additional ones you need
                    'a',
                    'b',
                    'blockquote',
                    'br',
                    'caption',
                    'code',
                    'div',
                    'em',
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'hr',
                    'i',
                    'img',
                    'li',
                    'ol',
                    'p',
                    'pre',
                    'small',
                    'span',
                    'strike',
                    'strong',
                    'sub',
                    'sup',
                    'table',
                    'tbody',
                    'td',
                    'tfoot',
                    'th',
                    'thead',
                    'tr',
                    'u',
                    'ul'
                ],
                FORBID_ATTR: [],
                ADD_ATTR: ['style', 'class', 'target'],
                ADD_TAGS: ['iframe'],
                KEEP_CONTENT: true
            });
            return sanitized;
        }
        return '';
    }, [VDKEventsForEventDetails?.details]);
    const getCollaboratorRoleLabel = (role) => {
        switch (role) {
            case CollaboratorRole.TransportManager:
                return t('collaborators.roleTransportManager');
            case CollaboratorRole.Collaborator:
            default:
                return t('collaborators.roleCollaborator');
        }
    };
    // console.log('VDKEventsForEventDetails:', VDKEventsForEventDetails?.partnerName);
    return (_jsx(_Fragment, { children: _jsx(Box, { height: 'calc(100dvh - 146px)', sx: { overflowY: 'auto' }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12, lg: 6, xxl: 4 }, children: VDKEventsForEventDetails?.assignedUsers && (_jsx(AvatarBox, { labelIcon: _jsx(UserSquare, {}), 
                            // subheader={`${VDKEventsForEventDetails.assignedUsers[0]?.firstName} ${VDKEventsForEventDetails.assignedUsers[0]?.lastName}`}
                            header: VDKEventsForEventDetails.assignedUsers.length > 1
                                ? t('events.eventDetails.generalInfoTab.happinessOfficers')
                                : t('events.eventDetails.generalInfoTab.happinessOfficer'), avatarSrc: VDKEventsForEventDetails.assignedUsers[0]?.profilePictureUrl || '', showGroupAvatar: true, groupAvatars: VDKEventsForEventDetails.assignedUsers.map((user) => ({
                                avatar: user.profilePictureUrl || '',
                                name: `${user.firstName} ${user.lastName}`,
                                id: user.id,
                                roles: user.roles
                            })), noItemsLabel: t('events.eventDetails.generalInfoTab.noHappinessOfficersAssigned') })) }), _jsx(Grid, { size: { xs: 12, lg: 6, xxl: 4 }, children: VDKEventsForEventDetails?.assignedCollaborators && (_jsx(AvatarBox, { labelIcon: _jsx(Icon, { icon: 'fluent:people-48-regular' }), 
                            // subheader={`${VDKEventsForEventDetails.assignedUsers[0]?.firstName} ${VDKEventsForEventDetails.assignedUsers[0]?.lastName}`}
                            header: t('events.eventDetails.generalInfoTab.collaborators'), avatarSrc: VDKEventsForEventDetails.assignedCollaborators[0]?.profilePictureUrl || '', showGroupAvatar: true, groupAvatars: VDKEventsForEventDetails.assignedCollaborators.map((user) => ({
                                avatar: user.profilePictureUrl || '',
                                name: `${user.firstName} ${user.lastName}`,
                                id: user.id,
                                roles: [{ displayName: getCollaboratorRoleLabel(user.role) }]
                            })), noItemsLabel: t('events.eventDetails.generalInfoTab.noCollaboratorsAssigned') })) }), VDKEventsForEventDetails?.partnerId && (_jsx(Grid, { size: { xs: 12, lg: 6, xxl: 4 }, children: _jsx(AvatarBox, { labelIcon: _jsx(Icon, { icon: 'ph:users-three' }), header: t('events.eventDetails.generalInfoTab.partners') || 'Partners', avatarSrc: VDKEventsForEventDetails.partnerLogoUrl || '', showGroupAvatar: false, subheader: VDKEventsForEventDetails?.partnerName || 'Partner', groupAvatars: [
                                {
                                    avatar: VDKEventsForEventDetails.partnerLogoUrl || '',
                                    name: VDKEventsForEventDetails.partnerName || 'Partner'
                                }
                            ], noItemsLabel: t('events.eventDetails.generalInfoTab.noPartnersAssigned') ||
                                'No partners assigned' }) })), _jsx(Grid, { size: { xs: 12, lg: 6, xxl: 4 }, children: _jsx(AvatarBox, { labelIcon: _jsx(Calendar, {}), subheader: formatDateRange(VDKEventsForEventDetails?.startDate, VDKEventsForEventDetails?.endDate), header: t('events.eventDetails.generalInfoTab.eventDate'), 
                            //onActionClick={() => handleEditClick('labels.eventDuration')}
                            icon: _jsx(Calendar, { variant: 'Bulk', size: 48, color: theme.palette.primary.main }) }) }), _jsx(Grid, { size: { xs: 12, lg: 6, xxl: 4 }, children: _jsx(AvatarBox, { labelIcon: _jsx(Icon, { icon: 'hugeicons:building-05' }), subheader: VDKEventsForEventDetails?.eventCompanies?.length.toString() || '0', header: t('common.companies'), 
                            //onActionClick={() => handleEditClick('labels.company')}
                            tooltipItems: VDKEventsForEventDetails?.eventCompanies?.map((c) => c.label) || [], icon: _jsx(Buildings, { variant: 'Bulk', size: 48, color: theme.palette.primary.main }) }) }), _jsx(Grid, { size: { xs: 12, lg: 6, xxl: 4 }, children: _jsx(AvatarBox, { labelIcon: _jsx(People, {}), subheader: VDKEventsForEventDetails?.isExternalAllowed
                                ? String(t('events.createNewEvent.yes'))
                                : String(t('events.createNewEvent.no')), header: t('labels.externalsAllowed'), 
                            //onActionClick={() => handleEditClick('labels.assignTo')}
                            icon: _jsx(UserCirlceAdd, { variant: 'Bulk', size: 48, color: theme.palette.primary.main }) }) }), _jsx(Grid, { size: { xs: 12, lg: 6, xxl: 4 }, children: _jsx(AvatarBox, { labelIcon: _jsx(KeySquare, {}), subheader: VDKEventsForEventDetails?.isPasswordProtection
                                ? String(t('events.createNewEvent.yes'))
                                : String(t('events.createNewEvent.no')), header: t('labels.eventLinkAndPassword'), 
                            // onActionClick={() => handleEditClick('labels.eventLinkAndPassword')}
                            icon: _jsx(Lock, { variant: 'Bulk', size: 48, color: theme.palette.primary.main }) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(EventCard, { variant: 'light', disableShadow: true, disableBackground: true, heading: t('events.eventDetails.generalInfoTab.eventDescription'), bgPosition: 'top 30px left 108px', children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: '12px', children: _jsx(TextEditor, { value: cleanDescriptionDetails, readOnly: true, variant: 'white' }) }) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(EventCard, { disableShadow: true, disableBackground: true, variant: 'light', heading: t('events.eventDetails.generalInfoTab.extraInformation'), cardHeaderPadding: '0px 16px 0px 16px', children: VDKEventsForEventDetails?.extraInformations &&
                                VDKEventsForEventDetails.extraInformations.length > 0 ? (_jsx(Box, { display: 'flex', flexDirection: 'column', gap: '8px', children: VDKEventsForEventDetails.extraInformations.map((extraInfo, index) => (_jsxs(Accordion, { sx: {
                                        boxShadow: 'none',
                                        borderRadius: '0px',
                                        '&::before': {
                                            display: 'none'
                                        },
                                        ...(index !==
                                            (VDKEventsForEventDetails.extraInformations?.length ?? 0) - 1 && {
                                            '&.Mui-expanded': {
                                                borderBottom: `1px solid ${theme.palette.divider}`
                                            }
                                        })
                                    }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMore, {}), sx: {
                                                paddingLeft: 0
                                            }, children: _jsx(Typography, { variant: 'subtitle1', children: extraInfo.title }) }), _jsx(AccordionDetails, { style: { paddingLeft: 0 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', children: [extraInfo.description && (_jsx(Box, { children: _jsx(Typography, { variant: 'body1', children: extraInfo.description }) })), extraInfo.documentUrl && (_jsx(Box, { children: isVideoDocument(extraInfo.documentUrl, extraInfo.documentName) ? (_jsx("video", { src: extraInfo.documentUrl, width: 536, height: 352, controls: true, style: { objectFit: 'contain' } })) : isPdfDocument(extraInfo.documentUrl, extraInfo.documentName) ? (_jsxs(Box, { component: 'a', href: extraInfo.documentUrl, target: '_blank', rel: 'noopener noreferrer', title: extraInfo.documentName || undefined, sx: {
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                py: 0.5,
                                                                color: 'inherit',
                                                                textDecoration: 'none',
                                                                cursor: 'pointer',
                                                                '&:hover .pdf-document-name': {
                                                                    textDecoration: 'underline'
                                                                }
                                                            }, children: [_jsx(PdfIcon, { width: '20', height: '20' }), _jsx(Typography, { className: 'pdf-document-name', variant: 'body2', color: 'primary', children: extraInfo.documentName || String(t('common.pdfPreview')) })] })) : (_jsx("img", { src: extraInfo.documentUrl, alt: extraInfo.documentName || 'Document', width: 536, height: 352, style: { objectFit: 'contain' } })) }))] }) })] }, extraInfo.id))) })) : (_jsx(Typography, { variant: 'body2', color: 'textSecondary', pt: '12px', children: t('events.eventDetails.generalInfoTab.noExtraInformation') })) }) })] }) }) }));
});
export default DetailsTab;
